/**
 * IDE Detection Service
 *
 * Automatically detects which IDEs are active in the current project
 * based on the presence of IDE-specific directories and files.
 */

import { promises as fs } from 'fs';
import { join } from 'path';
import { IDE_FORMATS, type IdeFormat } from '../config/ide-formats.js';

/** Default framework rule file keyword — files containing this are excluded when checking if rules are "configured" */
const DEFAULT_RULE_KEYWORD = 'jai1';

export interface DetectedIDE {
    id: string;
    name: string;
    detected: boolean;
    hasRules: boolean;
    hasWorkflows: boolean;
    ruleCount: number;
    workflowCount: number;
    confidence: 'high' | 'medium' | 'low';
}

export class IdeDetectionService {
    private projectPath: string;

    constructor(projectPath: string = process.cwd()) {
        this.projectPath = projectPath;
    }

    /**
     * Detect all active IDEs in the project
     */
    async detectActiveIdes(): Promise<DetectedIDE[]> {
        const detected: DetectedIDE[] = [];

        for (const [ideId, format] of Object.entries(IDE_FORMATS)) {
            const detection = await this.detectIde(ideId, format);
            detected.push(detection);
        }

        return detected.filter((d) => d.detected);
    }

    /**
     * Detect a specific IDE
     */
    async detectIde(ideId: string, format: IdeFormat): Promise<DetectedIDE> {
        const detection: DetectedIDE = {
            id: ideId,
            name: format.name,
            detected: false,
            hasRules: false,
            hasWorkflows: false,
            ruleCount: 0,
            workflowCount: 0,
            confidence: 'low',
        };

        // Special handling for AGENTS.md and GEMINI.md
        if (ideId === 'agentsmd') {
            const agentsPath = join(this.projectPath, 'AGENTS.md');
            const exists = await this.pathExists(agentsPath);
            detection.detected = exists;

            if (exists) {
                const customCount = await this.countAgentsMdCustomSections(agentsPath);
                detection.hasRules = customCount > 0;
                detection.ruleCount = customCount;
            }

            detection.confidence = exists ? (detection.hasRules ? 'high' : 'medium') : 'low';
            return detection;
        }

        if (ideId === 'gemini') {
            const geminiPath = join(this.projectPath, 'GEMINI.md');
            const exists = await this.pathExists(geminiPath);
            detection.detected = exists;

            if (exists) {
                // Gemini references AGENTS.md, so check AGENTS.md content
                const agentsPath = join(this.projectPath, 'AGENTS.md');
                const agentsExists = await this.pathExists(agentsPath);
                if (agentsExists) {
                    const customCount = await this.countAgentsMdCustomSections(agentsPath);
                    detection.hasRules = customCount > 0;
                    detection.ruleCount = customCount;
                }
            }

            detection.confidence = exists ? (detection.hasRules ? 'high' : 'medium') : 'low';
            return detection;
        }

        // Special handling for OpenCode (AGENTS.md + .opencode/command/)
        if (ideId === 'opencode') {
            const agentsPath = join(this.projectPath, 'AGENTS.md');
            const hasAgents = await this.pathExists(agentsPath);
            const commandPath = join(this.projectPath, '.opencode/command');
            const hasCommands = await this.pathExists(commandPath);

            if (hasAgents) {
                const customCount = await this.countAgentsMdCustomSections(agentsPath);
                detection.hasRules = customCount > 0;
                detection.ruleCount = customCount;
            }

            if (hasCommands) {
                detection.hasWorkflows = true;
                detection.workflowCount = await this.countFiles(commandPath, '.md');
            }

            detection.detected = hasAgents || (hasCommands && detection.workflowCount > 0);
            detection.confidence = detection.detected ? (hasAgents && hasCommands ? 'high' : 'medium') : 'low';
            return detection;
        }

        // Special handling for Codex (AGENTS.md + .agents/skills/ + .codex/config.toml)
        if (ideId === 'codex') {
            const agentsPath = join(this.projectPath, 'AGENTS.md');
            const hasAgents = await this.pathExists(agentsPath);
            const skillsPath = join(this.projectPath, '.agents/skills');
            const hasSkills = await this.pathExists(skillsPath);
            const configPath = join(this.projectPath, '.codex/config.toml');
            const hasConfig = await this.pathExists(configPath);

            if (hasAgents) {
                const customCount = await this.countAgentsMdCustomSections(agentsPath);
                detection.hasRules = customCount > 0;
                detection.ruleCount = customCount;
            }

            if (hasSkills) {
                detection.hasWorkflows = true;
                detection.workflowCount = await this.countFiles(skillsPath, 'SKILL.md');
            }

            detection.detected = hasAgents || hasConfig || (hasSkills && detection.workflowCount > 0);
            detection.confidence = detection.detected
                ? (hasConfig || (hasAgents && detection.workflowCount > 0) ? 'high' : 'medium')
                : 'low';
            return detection;
        }

        // Check for IDE-specific directories
        const rulesPath = join(this.projectPath, format.rulesPath);
        const rulesExist = await this.pathExists(rulesPath);

        if (rulesExist) {
            const totalRules = await this.countFiles(rulesPath, format.fileExtension);
            const customRules = await this.countFiles(rulesPath, format.fileExtension, [DEFAULT_RULE_KEYWORD]);

            // hasRules is true only if there are custom (non-default) rules
            detection.hasRules = customRules > 0;
            detection.ruleCount = customRules;

            if (totalRules > 0) {
                detection.detected = true;
            }
        }

        // Check for workflows/commands if supported
        if (format.workflowsPath) {
            const workflowsPath = join(this.projectPath, format.workflowsPath);
            const workflowsExist = await this.pathExists(workflowsPath);

            if (workflowsExist) {
                detection.hasWorkflows = true;
                // Use workflowsFileExtension if defined (e.g., Cursor: rules=.mdc, commands=.md)
                const wfExtension = format.workflowsFileExtension ?? format.fileExtension;
                detection.workflowCount = await this.countFiles(workflowsPath, wfExtension);

                if (detection.workflowCount > 0) {
                    detection.detected = true;
                }
            }
        }

        // Determine confidence level
        if (detection.ruleCount > 3 || detection.workflowCount > 0) {
            detection.confidence = 'high';
        } else if (detection.ruleCount > 0) {
            detection.confidence = 'medium';
        }

        return detection;
    }

    /**
     * Get the most likely active IDE (highest confidence)
     */
    async getPrimaryIde(): Promise<DetectedIDE | null> {
        const active = await this.detectActiveIdes();

        if (active.length === 0) {
            return null;
        }

        // Sort by confidence and rule count
        active.sort((a, b) => {
            const confidenceScore = { high: 3, medium: 2, low: 1 };
            const aScore = confidenceScore[a.confidence] * 10 + a.ruleCount;
            const bScore = confidenceScore[b.confidence] * 10 + b.ruleCount;
            return bScore - aScore;
        });

        return active[0];
    }

    /**
     * Get IDE IDs that are active
     */
    async getActiveIdeIds(): Promise<string[]> {
        const active = await this.detectActiveIdes();
        return active.map((d) => d.id);
    }

    /**
     * Check if a specific IDE is active
     */
    async isIdeActive(ideId: string): Promise<boolean> {
        const format = IDE_FORMATS[ideId];
        if (!format) {
            return false;
        }

        const detection = await this.detectIde(ideId, format);
        return detection.detected;
    }

    /**
     * Detect IDEs with existing rule files (for backward compatibility)
     */
    async detectExistingIdes(): Promise<string[]> {
        const detected: string[] = [];

        for (const [ideId, format] of Object.entries(IDE_FORMATS)) {
            if (ideId === 'agentsmd') {
                if (await this.pathExists('AGENTS.md')) {
                    detected.push(ideId);
                }
            } else if (ideId === 'gemini') {
                if (await this.pathExists('GEMINI.md')) {
                    detected.push(ideId);
                }
            } else if (ideId === 'opencode') {
                // OpenCode uses AGENTS.md + .opencode/command/
                const hasAgents = await this.pathExists('AGENTS.md');
                const commandPath = join(this.projectPath, '.opencode/command');
                const hasCommands = await this.pathExists(commandPath);
                if (hasAgents || hasCommands) {
                    detected.push(ideId);
                }
            } else if (ideId === 'codex') {
                const hasAgents = await this.pathExists('AGENTS.md');
                const hasConfig = await this.pathExists(join(this.projectPath, '.codex/config.toml'));
                const hasSkills = await this.pathExists(join(this.projectPath, '.agents/skills'));
                if (hasAgents || hasConfig || hasSkills) {
                    detected.push(ideId);
                }
            } else {
                const rulesPath = join(this.projectPath, format.rulesPath);
                if (await this.pathExists(rulesPath)) {
                    const count = await this.countFiles(rulesPath, format.fileExtension);
                    if (count > 0) {
                        detected.push(ideId);
                    }
                }
            }
        }

        return detected;
    }

    /**
     * Get detection summary
     */
    async getSummary(): Promise<{
        total: number;
        active: DetectedIDE[];
        inactive: string[];
        primary: DetectedIDE | null;
    }> {
        const all = await Promise.all(
            Object.entries(IDE_FORMATS).map(([ideId, format]) => this.detectIde(ideId, format))
        );

        const active = all.filter((d) => d.detected);
        const inactive = all.filter((d) => !d.detected).map((d) => d.id);
        const primary = await this.getPrimaryIde();

        return {
            total: all.length,
            active,
            inactive,
            primary,
        };
    }

    /**
     * Check if a path exists
     */
    private async pathExists(path: string): Promise<boolean> {
        try {
            await fs.access(path);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Count files with specific extension in a directory (recursive)
     * Supports both flat files and files in subdirectories
     */
    private async countFiles(
        dirPath: string,
        extension: string,
        excludeKeywords: string[] = []
    ): Promise<number> {
        try {
            const entries = await fs.readdir(dirPath, { withFileTypes: true });
            let count = 0;

            for (const entry of entries) {
                if (entry.isDirectory()) {
                    // Recurse into subdirectories
                    count += await this.countFiles(
                        join(dirPath, entry.name),
                        extension,
                        excludeKeywords
                    );
                } else if (entry.isFile() && entry.name.endsWith(extension)) {
                    // Exclude files whose name contains any of the given keywords
                    if (excludeKeywords.length > 0) {
                        const nameLower = entry.name.toLowerCase();
                        if (excludeKeywords.some((kw) => nameLower.includes(kw))) {
                            continue;
                        }
                    }
                    count++;
                }
            }

            return count;
        } catch {
            return 0;
        }
    }

    /**
     * Count custom (non-jai1) sections in AGENTS.md
     * Counts H2 headings (## ) that do not contain the keyword 'jai1'
     */
    private async countAgentsMdCustomSections(filePath: string): Promise<number> {
        try {
            const content = await fs.readFile(filePath, 'utf-8');
            const h2Headings = content.match(/^## .+$/gm) || [];
            return h2Headings.filter(
                (h) => !h.toLowerCase().includes(DEFAULT_RULE_KEYWORD)
            ).length;
        } catch {
            return 0;
        }
    }

    /**
     * Get IDE format by ID
     */
    getIdeFormat(ideId: string): IdeFormat | undefined {
        return IDE_FORMATS[ideId];
    }

    /**
     * Suggest IDEs to configure based on project structure
     */
    async suggestIdes(): Promise<
        Array<{
            ideId: string;
            name: string;
            reason: string;
            priority: 'high' | 'medium' | 'low';
        }>
    > {
        const suggestions: Array<{
            ideId: string;
            name: string;
            reason: string;
            priority: 'high' | 'medium' | 'low';
        }> = [];

        // Check for .vscode - suggest VSCode-based IDEs
        if (await this.pathExists(join(this.projectPath, '.vscode'))) {
            suggestions.push({
                ideId: 'cursor',
                name: 'Cursor',
                reason: 'VSCode configuration detected',
                priority: 'high',
            });
        }

        // Check for package.json - web project
        if (await this.pathExists(join(this.projectPath, 'package.json'))) {
            suggestions.push({
                ideId: 'windsurf',
                name: 'Windsurf',
                reason: 'JavaScript/TypeScript project detected',
                priority: 'medium',
            });
        }

        // Always suggest AGENTS.md as universal
        suggestions.push({
            ideId: 'agentsmd',
            name: 'AGENTS.md',
            reason: 'Universal format, works with all IDEs',
            priority: 'high',
        });

        // Check for existing AI config files
        const hasClaudeConfig = await this.pathExists(join(this.projectPath, '.claude'));
        if (hasClaudeConfig) {
            suggestions.push({
                ideId: 'claude',
                name: 'Claude Code',
                reason: '.claude directory detected',
                priority: 'high',
            });
        }

        // Check for .opencode directory
        const hasOpenCodeConfig = await this.pathExists(join(this.projectPath, '.opencode'));
        if (hasOpenCodeConfig) {
            suggestions.push({
                ideId: 'opencode',
                name: 'OpenCode',
                reason: '.opencode directory detected',
                priority: 'high',
            });
        }

        const hasCodexConfig = await this.pathExists(join(this.projectPath, '.codex'));
        if (hasCodexConfig) {
            suggestions.push({
                ideId: 'codex',
                name: 'Codex',
                reason: '.codex directory detected',
                priority: 'high',
            });
        }

        return suggestions;
    }
}
